import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";

import { Loader } from "./loader";
import { getObjLength } from "../helpers/utils";
import { postAPIPayload } from "../../apis/postApi";
import auto_activity1 from "../../assets/home/auto_activity_zone.svg";
import { userSigupAction } from "../../redux/slices/user/userSignUpSlice";
import {
  PER_ANNUAL_SAVINGS,
  PER_MONTH_SAVINGS,
  TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS,
} from "../helpers/constants";
import { Button as TailwindButton } from "./customDesignedComponents/button";
import { OrderSummaryBreakdown } from "./customDesignedComponents/OrderSummaryModal";

import "./Style.scss";

const InvestorsAnnaulSubscriptionPlan = ({
  uid = null,
  paySecure = () => {},
  propertiesIds = [],
  promoCode = undefined,
  selectedData = undefined,
  isBack_btn = false,
  backBtnFunction = () => {},
  tabComponent = false,
  isTailwindComponent = false,
  // Price exactly these ids (County Investor page) rather than the signup list.
  preferGivenPropertyIds = false,
  // County Investor page: price with get-final-amount, the same pricing the
  // charge uses; investors-price-details then only supplies the plan.
  finalAmountPricing = false,
}) => {
  const dispatch = useDispatch();
  const [yearlyPlanData, setYearlyPlanData] = useState({});
  const [pricingBreakdown, setPricingBreakdown] = useState({});
  const [propertiesCount, setPropertiesCount] = useState(0);
  const [isError, setIsError] = useState(false);
  const [finalAmountData, setFinalAmountData] = useState(null);
  const [finalAmountError, setFinalAmountError] = useState("");
  const [effectivePromoCode, setEffectivePromoCode] = useState(promoCode);
  const { propertyIds, investorsAnnualPlanData, userLoginLoader } = useSelector(
    (state) => state.signUpUser,
  );
  const pricedPropertyIds =
    preferGivenPropertyIds && propertiesIds?.length
      ? propertiesIds
      : propertyIds?.length > 0
        ? propertyIds
        : propertiesIds;

  const activatePlan = () => {
    dispatch(userSigupAction.updateInvestorsPricingUI(false));
    paySecure({
      ...yearlyPlanData,
      promo_code: finalAmountPricing ? effectivePromoCode : promoCode,
      final_amount: finalAmountPricing ? finalAmountData : undefined,
    });
  };
  useEffect(() => {
    dispatch(userSigupAction.updateInvestorsPricingUI(true));
    dispatch(
      userSigupAction.getInvestorsAnnualPlanData({
        url: "investors-price-details",
        data: {
          user_id: uid,
          property_id: pricedPropertyIds,
          property_ids: getObjLength(selectedData)
            ? [
                {
                  address: selectedData?.address,
                  property_id: selectedData?.property_id,
                  state: selectedData?.state,
                  city: selectedData?.city,
                  county: selectedData?.county,
                  zip: selectedData?.zip,
                },
              ]
            : undefined,
          promo_code: finalAmountPricing ? undefined : promoCode,
        },
        type: "post",
      }),
    );
  }, []);

  // get-final-amount for the plan's properties, with the promo when there is one.
  useEffect(() => {
    const planId = yearlyPlanData?.plan_id;
    if (!finalAmountPricing || !planId) {
      return undefined;
    }
    let cancelled = false;
    const fetchFinalAmount = (code) =>
      postAPIPayload({
        url: "get-final-amount",
        data: {
          user_id: uid,
          plan_id: planId,
          property_id: pricedPropertyIds,
          // An empty promo_code counts as an invalid code: leave it out.
          ...(code ? { promo_code: code } : {}),
        },
      });
    (async () => {
      let code = promoCode || undefined;
      let response = await fetchFinalAmount(code);
      // A stale county default code shouldn't block payment: price without it.
      if (!response?.success && code) {
        code = undefined;
        response = await fetchFinalAmount();
      }
      if (cancelled) {
        return;
      }
      if (response?.success) {
        setFinalAmountData(response.data);
        setEffectivePromoCode(code);
        setFinalAmountError("");
        return;
      }
      setFinalAmountData(null);
      setFinalAmountError(
        response?.data?.message ||
          response?.message ||
          "We could not load your pricing. Please try again.",
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [finalAmountPricing, yearlyPlanData?.plan_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (getObjLength(investorsAnnualPlanData)) {
      if (investorsAnnualPlanData?.success) {
        setYearlyPlanData(investorsAnnualPlanData?.data?.[0]);
        setPricingBreakdown(
          investorsAnnualPlanData?.data?.[0]?.price_breakdown,
        );
        setPropertiesCount(
          parseInt(
            investorsAnnualPlanData?.data?.[0]?.price_breakdown
              ?.no_of_properties,
          ) || "-",
        );
        setIsError(false);
      } else {
        setIsError(true);
      }
    }
  }, [investorsAnnualPlanData]);

  const toAmount = (value) =>
    Number(String(value ?? "0").replace(/,/g, "")) || 0;
  const discountPercentLabel =
    propertiesCount > 4 && propertiesCount < 9
      ? "20%"
      : propertiesCount > 9
        ? "30%"
        : "10%";
  const hasMultiPropertyDiscount = toAmount(pricingBreakdown?.discount) > 0;
  const formatPlanPrice = (cents) =>
    getObjLength(yearlyPlanData) ? "$" + (cents / 100).toFixed(2) : "-";

  if (isTailwindComponent) {
    const breakdownRows = [
      {
        label: "Properties",
        value: `${propertiesCount || "-"} x ${formatPlanPrice(yearlyPlanData?.amount)}`,
        amount: `$${pricingBreakdown?.subscription_amount_actual ?? 0}`,
      },
      ...(hasMultiPropertyDiscount
        ? [
            {
              label: "Multi-property discount",
              value: discountPercentLabel,
              amount: `-$${pricingBreakdown?.discount}`,
              highlight: true,
            },
          ]
        : []),
      ...(pricingBreakdown?.setup_fees_actual
        ? [
            {
              label: "Setup fee",
              amount: `$${pricingBreakdown?.setup_fees_actual}`,
            },
          ]
        : []),
      {
        label: "Recording fees (deposit)",
        amount: `$${pricingBreakdown?.recording_amount_actual ?? 0}`,
      },
      {
        label: "Sales / Local taxes",
        amount: `$${pricingBreakdown?.sales_tax_amount_actual ?? 0}`,
      },
      ...(promoCode && toAmount(pricingBreakdown?.promocode_discount) > 0
        ? [
            {
              label: "Promo code discount",
              amount: `-$${pricingBreakdown?.promocode_discount}`,
              highlight: true,
            },
          ]
        : []),
    ];

    return (
      <div className="tailwind_component w-full text-left">
        {isError || finalAmountError ? (
          <p className="py-6 text-center text-sm text-destructive">
            {finalAmountError ||
              investorsAnnualPlanData?.data?.message ||
              "We could not load your pricing. Please try again."}
          </p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            {/* Same plan card as the County Investor page's Preventative plan. */}
            <div className="overflow-hidden rounded-2xl bg-white shadow-lg ring-2 ring-[#AD376F]">
              <div className="bg-[#AD376F] py-2 text-center text-xs font-bold uppercase tracking-wider text-white">
                Complete Protection
              </div>
              <div className="bg-gradient-to-r from-[#AD376F] to-[#c9528a] px-6 py-5 text-center text-white">
                <div className="mb-1 text-lg font-semibold">
                  EquityProtect Yearly
                </div>
                <div className="text-4xl font-semibold">
                  {formatPlanPrice(yearlyPlanData?.recurring_amount)}
                </div>
                <div className="mt-1 text-xs tracking-wider">
                  PER YEAR, PER PROPERTY
                </div>
              </div>
              <ul className="space-y-2 px-6 py-5 text-sm text-gray-700">
                <li>+ $99.99 one-time setup fee</li>
                <li>+ applicable sales tax</li>
                <li>+ applicable deposit for recording fee (one-time fee)</li>
              </ul>
            </div>

            <div className="min-w-0">
              <h3 className="mb-3 text-base font-semibold text-foreground">
                Price breakdown
              </h3>
              {!finalAmountPricing ? (
                <OrderSummaryBreakdown
                  rows={breakdownRows}
                  totalLabel="Total to be charged"
                  totalAmount={String(pricingBreakdown?.total ?? "0")}
                />
              ) : finalAmountData ? (
                <OrderSummaryBreakdown
                  paymentData={finalAmountData}
                  totalLabel="Total to be charged"
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Calculating your total...
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          {isBack_btn ? (
            <TailwindButton
              type="button"
              variant="outline"
              className="rounded-full px-8"
              onClick={backBtnFunction}
            >
              Back
            </TailwindButton>
          ) : (
            <span />
          )}
          {!isError && !finalAmountError && (
            <TailwindButton
              type="button"
              className={TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS}
              onClick={activatePlan}
              disabled={
                !getObjLength(yearlyPlanData) ||
                (finalAmountPricing && !finalAmountData)
              }
            >
              CONTINUE TO PAYMENT
            </TailwindButton>
          )}
        </div>
        <Loader open={userLoginLoader} />
      </div>
    );
  }

  return (
    <Box className="investors_plans">
      <Box className="accountBoxWrapper">
        <Box className="investors_header">
          <Typography align="center" className="plan_header px-2">
            EquityProtect Yearly
          </Typography>
          <div className="px-2">
            <img src={auto_activity1} alt="auto_activity_zone" />
          </div>
        </Box>
        <Divider />
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {isError ? (
            <Box sx={{ width: "100%" }} className="text-center">
              <Stack direction="row" mt={3}>
                <p className="text-danger">
                  {investorsAnnualPlanData?.data?.message}
                </p>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ width: "100%" }} className="text-center">
              <Stack direction="row" mt={3}>
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    xl={6}
                    order={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2 }}
                    mb={2}
                  >
                    <Box
                      className="paymentMode plans-block w-100 d-flex justify-content-center align-items-center"
                      sx={{ margin: 0 }}
                    >
                      <div className={`planCard d-flex flex-column `}>
                        <div className={`card_inner w-100 break-word`}>
                          <div className="pricing_Wrapper text-center">
                            {/* <p className="para mb-1 activation_text mt-2">
                              No Set-up or Activation Fee
                            </p> */}
                            <h5 className={`mb-1 plan_price`}>
                              {getObjLength(yearlyPlanData)
                                ? "$" +
                                  (
                                    yearlyPlanData?.recurring_amount / 100
                                  ).toFixed(2)
                                : "-"}
                            </h5>
                            <p className="para mb-3">per year, per property</p>
                            <p className="para per_year_price ">
                              {getObjLength(yearlyPlanData) ? (
                                <>
                                  + <span className="fw-500">$</span>
                                  99.99 one-time setup fee
                                </>
                              ) : (
                                "-"
                              )}
                            </p>
                            {/* <p className="para mb-0 mt-0">
                              per year for annual recertification
                            </p> */}
                            <p className="para mb-0 mt-0">
                              + applicable sales tax
                            </p>
                            <p className="para mb-0 w-100">
                              + applicable Deposit for recording fee (One time
                              fee)
                            </p>
                            {/* <p className="para mt-3 activation_text">
                              ${PER_MONTH_SAVINGS} per month an annual savings
                              of ${PER_ANNUAL_SAVINGS}
                            </p> */}
                          </div>
                        </div>
                      </div>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    xl={6}
                    order={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2 }}
                    mb={2}
                  >
                    <Box
                      className="paymentMode plans-block w-100 d-flex justify-content-center align-items-center"
                      sx={{ margin: 0 }}
                    >
                      <div className={`planCard d-flex flex-column`}>
                        <div
                          className={`price_breakdown_card card_inner w-100 break-word`}
                        >
                          <div className="pricing_Wrapper text-start">
                            <p className="para mb-0 price_breakdown_text">
                              Price breakdown
                            </p>
                            <div className="pricing_cards">
                              <p className={`para mb-0 price_breakdown_left`}>
                                {propertiesCount || "-"}{" "}
                                {propertiesCount === 1
                                  ? "Property"
                                  : "Properties"}{" "}
                                x{" "}
                                {getObjLength(yearlyPlanData)
                                  ? "$" +
                                    (yearlyPlanData?.amount / 100).toFixed(2)
                                  : "-"}
                              </p>
                              <p className="para mb-0 price_breakdown_right">
                                $
                                {pricingBreakdown?.subscription_amount_actual ||
                                  "-"}
                              </p>
                            </div>
                            {hasMultiPropertyDiscount && (
                              <div className="pricing_cards discount_section">
                                <p className={`para mb-0 price_breakdown_left`}>
                                  {discountPercentLabel} multi-property Discount
                                </p>
                                <p className="para mb-0 price_breakdown_right">
                                  -${pricingBreakdown?.discount || ""}
                                </p>
                              </div>
                            )}
                            {/* <div className="pricing_cards">
                              <p className={`para mb-0 price_breakdown_left`}>
                                {propertiesCount || ""}{" "}
                                {propertiesCount === 1
                                  ? "Property"
                                  : "Properties"}{" "}
                                Total
                              </p>
                              <p className="para mb-0 price_breakdown_right">
                                ${pricingBreakdown?.sub_amount || ""}
                              </p>
                            </div> */}
                            {pricingBreakdown?.setup_fees_actual && (
                              <div className="pricing_cards">
                                <p className={`para mb-0 price_breakdown_left`}>
                                  Setup Fee
                                </p>
                                <p className="para mb-0 price_breakdown_right">
                                  ${pricingBreakdown?.setup_fees_actual || ""}
                                </p>
                              </div>
                            )}
                            <div className="pricing_cards">
                              <p className={`para mb-0 price_breakdown_left`}>
                                Recording Fee
                              </p>
                              <p className="para mb-0 price_breakdown_right">
                                $
                                {pricingBreakdown?.recording_amount_actual ||
                                  ""}
                              </p>
                            </div>
                            <div className="pricing_cards">
                              <p className={`para mb-0 price_breakdown_left`}>
                                State Sales Tax
                              </p>
                              <p className="para mb-0 price_breakdown_right">
                                $
                                {pricingBreakdown?.sales_tax_amount_actual ||
                                  ""}
                              </p>
                            </div>
                            {promoCode && (
                              <div className="pricing_cards discount_section">
                                <p className={`para mb-0 price_breakdown_left`}>
                                  Applied Promo Code Discount
                                </p>
                                <p className="para mb-0 price_breakdown_right">
                                  -${pricingBreakdown?.promocode_discount || ""}
                                </p>
                              </div>
                            )}
                            <Divider className="total_amt_divider" />
                            <div className="pricing_cards total_amount">
                              <p className={`para mb-0 price_breakdown_left `}>
                                Total to be charged
                              </p>
                              <p className="para mb-0 price_breakdown_right">
                                ${pricingBreakdown?.total || ""}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Box>
                  </Grid>
                </Grid>
              </Stack>
              {isBack_btn && (
                <Button
                  className="other_popup_btn popup_Btn m-1"
                  onClick={backBtnFunction}
                >
                  Back
                </Button>
              )}
              <Button
                className="success_popup_btn popup_Btn m-1"
                onClick={activatePlan}
              >
                Activate plan
              </Button>
            </Box>
          )}
        </Grid>
      </Box>
      <Loader open={userLoginLoader} />
    </Box>
  );
};

export default InvestorsAnnaulSubscriptionPlan;
